import type {
	AccountConstructionEstimate,
	AccountsApiResponse,
	PhoneNumberApiResponse
} from '@/components/account/types'
import type { TableFilter, TableRow } from '@/components/table/types'
import type { MappedZone } from '@/components/zones/types'
import { INACTIVE } from '@/constants/accounts/statuses'
import { mapServiceLocationFeatures as mapServiceLocationFeaturesCore } from '@accounts/utils'
import type { AccountStatus } from '@/types/account-statuses'
import type { MappedTag, TableTag } from '@/types/tags'
import { formatPhone } from '@/utils/formatPhone'
import { mapTags } from '@/utils/mappers/TagMap'
import type {
	PortalMapAccountListResponse,
	ServiceLocationProperties
} from '@accounts/types'

const getMainlineCompleted = (zone: AccountsApiResponse['zone']): string => {
	return (zone?.constructionEstimates?.find(
		(estimate: AccountConstructionEstimate) =>
			estimate?.title === 'mainline')?.text === 'completed').toString() || 'false'
}

const mapAccountsTable = (accounts: AccountsApiResponse[]): TableRow[] => {
	return (accounts ?? []).map(({
		accountId,
		accountStatus,
		owner,
		address,
		serviceDetails,
		zone,
		moveInDate,
		accountCreatedTime,
		ignoreOverdue,
		stickyNote,
		activatedTime,
		equipment,
		isDelinquent,
		tags
	}) => {
		return {
			id: accountId?.toString(),
			name: `${owner?.first || ''} ${owner?.last || ''}`,
			address: `${address?.address1}` || '',
			neighborhood: zone?.name || '',
			email: owner?.email || '',
			phone: getPrimaryPhone(owner?.phones) || '',
			accountStatus: accountStatus || '',
			accountCreatedTime: formatDate(accountCreatedTime),
			activatedTime: formatDate(activatedTime),

			// Tags
			tagIds: mapTags(tags)?.map(tag => tag.id).join(',') || '',
			tagNames: mapTags(tags)?.map(tag => tag.name).join(',') || '',
			hasTags: mapTags(tags)?.length ? 'true' : 'false',
			tagCount: mapTags(tags)?.length.toString() || '0',

			hasLogin: owner?.hasLogin?.toString() || 'false',
			hasEquipment: equipment?.length ? 'true' : 'false',
			ignoreOverdue: formatDate(ignoreOverdue),
			isDelinquent: isDelinquent?.toString() || 'false',
			stickyNote: stickyNote || '',

			moveInDate: formatDate(moveInDate),
			priorityInstall: serviceDetails?.priorityInstall?.toString() || 'false',
			mainlineCompleted: getMainlineCompleted(zone),

			cableSpliced: serviceDetails?.cableSpliced?.toString() || 'false',
			cablePulled: serviceDetails?.cablePulled?.toString() || 'false',
			cableTerminated: serviceDetails?.cableTerminated?.toString() || 'false',

			insideInstalled: serviceDetails?.insideInstalled?.toString() || 'false',
			insideInstallStatus: getInsideInstallStatus(serviceDetails),
			insideScheduledDate: formatDate(serviceDetails?.insideScheduledDate),
			insideScheduledTime: serviceDetails?.insideScheduledTime || '',

			outsideInstalled: serviceDetails?.outsideInstalled?.toString() || 'false',
			outsideInstallStatus: serviceDetails?.outsideInstallStatus || '',
			outsideScheduledDate: formatDate(serviceDetails?.outsideScheduledDate),
			outsideScheduledTime: serviceDetails?.outsideScheduledTime || '',

			siteSurveyed: serviceDetails?.surveyed?.toString() || 'false',
			surveyScheduledDate: formatDate(serviceDetails?.surveyScheduledDate),
			surveyScheduledTime: serviceDetails?.surveyScheduledTime || '',
			surveyStatus: getSurveyStatus(serviceDetails),

			rowClasses: highlightPriorityInstall(accountStatus, serviceDetails) ? 'text-[#E9A337]' : '',
			navigateTo: `/accounts/${accountId?.toString()}`
		}
	})
}

const highlightPriorityInstall = (accountStatus: AccountStatus, serviceDetails: AccountsApiResponse['serviceDetails']): boolean => {
	return serviceDetails?.priorityInstall && !serviceDetails?.insideInstalled && accountStatus?.toLowerCase() !== INACTIVE.toLowerCase()
}

const getSurveyStatus = (serviceDetails: AccountsApiResponse['serviceDetails']): string => {
	if (serviceDetails?.surveyed) {
		return 'completed'
	}
	if (serviceDetails?.surveyScheduledDate) {
		return 'scheduled'
	}
	return 'needed'
}

const getInsideInstallStatus = (serviceDetails: AccountsApiResponse['serviceDetails']): string => {
	if (serviceDetails?.insideInstalled) {
		return 'completed'
	}
	if (serviceDetails?.insideScheduledDate) {
		return 'scheduled'
	}
	return 'needed'
}

const getPrimaryPhone = (phones: PhoneNumberApiResponse[]): string => {
	const primaryPhone = phones?.find(phone => phone?.isPrimary)
	return formatPhone(primaryPhone?.number) || ''
}

const mapZonesToNeighborhoodOptions = (zones: MappedZone[]): TableFilter[] => {
	if (!zones.length) {
		return []
	}
	const parentZones = zones?.filter(zone => zone?.parentZoneId === '0')?.map(zone => ({
		id: zone.id,
		name: zone?.name,
		count: zone?.addressCount
	}))
	const parentNeighborHoods: TableFilter[] = Array.from(new Set(parentZones))?.map(parentZone => ({
		id: parentZone.name || '',
		value: parentZone.id || '',
		label: `${parentZone.name} (${parentZone?.count})`.trim(),
		columnKey: 'neighborhood',
		matches: parentZone.name || '',
		children: []
	}))
	const childZones = zones?.filter(zone => zone?.parentZoneId !== '0').map(zone => ({
		id: zone.id,
		name: zone?.name,
		count: zone?.addressCount,
		parentId: zone?.parentZoneId
	}))
	childZones?.forEach(zone => {
		const parentZone = parentNeighborHoods.find(parent => parent.value === zone?.parentId)
		if (parentZone?.children) {
			parentZone.children.push({
				id: zone.name || '',
				value: zone.id || '',
				label: `${zone.name} (${zone?.count || ''})`.trim(),
				columnKey: 'neighborhood',
				matches: zone.name || ''
			})
		}
	})
	return parentNeighborHoods?.sort((a, b) => a.label.localeCompare(b.label))
}

const mapTagsToFilterOptions = (tags: MappedTag[], tableTags: TableTag[]): TableFilter[] => {
	if (!tags.length) {
		return []
	}
	const getTagLabel = (tag: MappedTag): string => {
		const tagName = tag.name || ''
		const tagCount = tableTags.find(t => t.name === tagName)?.accountIds?.length || 0
		if (tagCount === 0) {
			return tagName
		}
		return `${tagName} (${tagCount})`
	}
	const tagOptions: TableFilter[] = tags?.map(tag => ({
		id: tag.name || '',
		value: tag.name || '',
		label: getTagLabel(tag),
		columnKey: 'accountTag',
		matches: tag.id || ''
	}))
	return tagOptions?.sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Maps API response to ServiceLocationProperties
 * @deprecated Use mapServiceLocationFeaturesCore from AccountFeatureMapper instead
 * This wrapper is kept for backward compatibility
 */
const mapServiceLocationFeatures = (response: PortalMapAccountListResponse[]): ServiceLocationProperties[] => {
	return mapServiceLocationFeaturesCore(response)
}

export {
	mapAccountsTable, mapServiceLocationFeatures, mapTagsToFilterOptions, mapZonesToNeighborhoodOptions
}
